import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Crown, Loader2, CheckCircle, XCircle, LogIn } from 'lucide-react';
import { motion } from 'framer-motion';
import { toast } from 'sonner';

const LOGO_URL = '/logo.webp';
const API = process.env.REACT_APP_BACKEND_URL;

export const SubscriptionSuccessPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, api, refreshUser } = useAuth();
  const [status, setStatus] = useState('verifying'); // verifying | success | paid_anon | error
  const [message, setMessage] = useState('Verifying your subscription...');

  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    const planId = searchParams.get('plan_id') || 'monthly';

    if (!sessionId) {
      setStatus('error');
      setMessage('No session ID found');
      return;
    }

    if (user) {
      verifySubscription(sessionId, planId);
    } else {
      // Auth cookie was stripped on the cross-site Stripe redirect (common on
      // mobile browsers). Fall back to the public status endpoint so the user
      // at least sees a successful confirmation and a clear path to log in.
      verifyPublic(sessionId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const verifyPublic = async (sessionId) => {
    try {
      const res = await fetch(
        `${API}/api/payments/stripe/status?session_id=${encodeURIComponent(sessionId)}`,
        { credentials: 'include' },
      );
      const data = await res.json();
      if (data.paid) {
        setStatus('paid_anon');
        setMessage('Subscription payment confirmed!');
      } else {
        setStatus('error');
        setMessage('Payment not completed yet.');
      }
    } catch {
      setStatus('error');
      setMessage('Could not confirm payment.');
    }
  };

  const verifySubscription = async (sessionId, planId) => {
    let attempts = 0;
    const maxAttempts = 5;
    const pollInterval = 2000;

    const poll = async () => {
      attempts++;
      
      try {
        const res = await api().post(`/subscriptions/verify?session_id=${sessionId}&plan_id=${planId}`);
        
        if (res.data.success) {
          setStatus('success');
          setMessage('Your subscription is now active!');
          toast.success('Welcome to Premium!');
          if (refreshUser) await refreshUser();
          return;
        }
        
        if (attempts < maxAttempts) {
          setTimeout(poll, pollInterval);
        } else {
          setStatus('error');
          setMessage('Verification timed out. Please check your subscription status.');
        }
      } catch (err) {
        if (attempts < maxAttempts) {
          setTimeout(poll, pollInterval);
        } else {
          setStatus('error');
          setMessage('Failed to verify subscription. Please contact support.');
        }
      }
    };

    poll();
  };

  return (
    <div className="min-h-screen bg-black">
      <header className="sticky top-0 z-50 glass border-b border-border">
        <div className="container mx-auto px-6 py-4 flex items-center justify-center">
          <Link to={user ? '/dashboard' : '/'}>
            <img src={LOGO_URL} alt="Ascend & Surpass" className="h-10 w-10" />
          </Link>
        </div>
      </header>

      <main className="container mx-auto px-6 py-20">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="max-w-md mx-auto text-center"
        >
          {status === 'verifying' && (
            <>
              <Loader2 className="h-16 w-16 text-primary animate-spin mx-auto mb-6" />
              <h1 className="font-heading text-3xl text-white uppercase tracking-wider mb-4">
                Processing...
              </h1>
              <p className="text-muted-foreground">{message}</p>
            </>
          )}

          {status === 'success' && (
            <>
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ type: 'spring', bounce: 0.5 }}
              >
                <div className="relative inline-block mb-6">
                  <Crown className="h-20 w-20 text-primary" />
                  <CheckCircle className="h-8 w-8 text-green-500 absolute -bottom-1 -right-1 bg-black rounded-full" />
                </div>
              </motion.div>
              <h1 className="font-heading text-3xl text-white uppercase tracking-wider mb-4">
                Welcome to <span className="text-primary">Premium!</span>
              </h1>
              <p className="text-muted-foreground mb-8">{message}</p>
              <div className="space-y-3">
                <Button
                  onClick={() => navigate('/dashboard')}
                  className="w-full bg-primary text-black"
                  data-testid="subscription-success-dashboard-btn"
                >
                  Go to Dashboard
                </Button>
                <Button
                  onClick={() => navigate('/programs')}
                  variant="outline"
                  className="w-full border-border"
                >
                  Explore Programs
                </Button>
              </div>
            </>
          )}

          {/* Paid confirmed but user got signed out during the redirect */}
          {status === 'paid_anon' && (
            <>
              <div className="relative inline-block mb-6">
                <Crown className="h-20 w-20 text-primary" />
                <CheckCircle className="h-8 w-8 text-green-500 absolute -bottom-1 -right-1 bg-black rounded-full" />
              </div>
              <h1 className="font-heading text-3xl text-white uppercase tracking-wider mb-4">
                Payment <span className="text-primary">Confirmed</span>!
              </h1>
              <p className="text-muted-foreground mb-8">
                Your Premium subscription is active. Log in to start using it.
              </p>
              <Link to="/login">
                <Button className="w-full bg-primary text-black" data-testid="subscription-success-login-btn">
                  <LogIn className="h-4 w-4 mr-2" /> Log In to Continue
                </Button>
              </Link>
            </>
          )}

          {status === 'error' && (
            <>
              <XCircle className="h-16 w-16 text-red-500 mx-auto mb-6" />
              <h1 className="font-heading text-3xl text-white uppercase tracking-wider mb-4">
                Something Went Wrong
              </h1>
              <p className="text-muted-foreground mb-8">{message}</p>
              <div className="space-y-3">
                <Button
                  onClick={() => navigate(user ? '/subscription' : '/login')}
                  className="w-full bg-primary text-black"
                >
                  {user ? 'Check Subscription Status' : 'Log In'}
                </Button>
                <Button
                  onClick={() => navigate(user ? '/dashboard' : '/')}
                  variant="outline"
                  className="w-full border-border"
                >
                  {user ? 'Go to Dashboard' : 'Home'}
                </Button>
              </div>
            </>
          )}
        </motion.div>
      </main>
    </div>
  );
};

export default SubscriptionSuccessPage;
